'use client'

import { useEffect, useRef, useState } from 'react'

import Icon from '@/components/Icon/Icon'
import Button from '@/components/Button/Button'
import Input from '@/components/Input/Input'

import styles from './VerifyForm.module.scss'
import authStyles from './Auth.module.scss'
import classNames from 'classnames/bind'

const classes = classNames.bind(styles)
const authClasses = classNames.bind(authStyles)

export default ({ selectedType, setPassword, setError, sendRequest, resendRequest }) => {
    const boxes = useRef([])
    const passwordInput = useRef(null)

    const [code, setCode] = useState('')
    const [revealPassword, setRevealPassword] = useState(false)
    const [codeTimer, setCodeTimer] = useState(0)

    const submit = () => {
        setError(null)
        if (selectedType == 'password-reset') {
            setPassword(passwordInput.current.value)
            sendRequest(code, passwordInput.current.value)
            return
        }
        sendRequest(code)
    }

    const handlePasswordEyeClick = () => {
        setRevealPassword(!revealPassword)
        setTimeout(() => {
            passwordInput.current.focus()
        }, 50)
    }

    useEffect(() => {
        if (codeTimer > 0) {
            setTimeout(() => {
                setCodeTimer(codeTimer - 1)
            }, 1000)
        }
    }, [codeTimer])

    let subtext = ''

    switch (selectedType) {
        case 'password-reset':
            subtext =
                'Check your email your inbox for a password reset code from us and enter it and your new password below.'
            break
        case 'recaptcha-verify':
            subtext =
                'Looks like our systems think you might be a robot. Please confirm you are not a robot by submitting the verification code sent to your email below.'
            break
        default:
            subtext =
                'Final steps! Please confirm the verification code sent to your email. If you are having issues receiving your code, please refresh the page and attempt signup again.'
            break
    }

    return (
        <form
            className={classes('verify-form')}
            onSubmit={(e) => {
                // Handle the form submission to register the user.
                e.preventDefault()
                if (code.length == 6) {
                    submit()
                }
            }}
            //onKeyUp={({ key }) => key == 'Enter' && code.length == 6 && sendRequest()}
            autoComplete="off"
        >
            <h1 className={classes('title')}>We'll need to verify your email</h1>
            <p className={classes('subtext')}>{subtext}</p>
            <div
                className={classes('verification-input-container')}
                onKeyDown={(e) => {
                    processBoxMovement(e)
                    setTimeout(() => {
                        setCode(boxes.current.map((box) => box.value).join(''))
                    }, 50)
                }}
            >
                {new Array(6).fill(0).map((_, i) => {
                    return (
                        <input
                            key={`code-box-${i}`}
                            className={classes('verify-input')}
                            type="number"
                            maxLength={1}
                            ref={(el) => (boxes.current[i] = el)}
                            autoComplete="off"
                            min="0"
                            max="9"
                            onChange={({ target }) => {
                                if (target.value.length > 1) {
                                    const characterArray = target.value.split('')
                                    let element = target
                                    let count = 0
                                    while (element) {
                                        element.value = characterArray[count]
                                        element = element.nextElementSibling
                                        count++
                                    }
                                }
                                setTimeout(() => {
                                    setCode(boxes.current.map((box) => box.value).join(''))
                                }, 50)
                            }}
                        />
                    )
                })}
            </div>
            <div className={classes('clear-button')}>
                <span
                    onClick={(e) => {
                        boxes.current.forEach((box) => {
                            box.value = ''
                        })
                        boxes.current[0].focus()
                        setCode('')
                    }}
                >
                    Clear
                </span>
            </div>

            {selectedType == 'password-reset' && (
                <div className={authClasses('input-container')}>
                    <Input
                        type={revealPassword ? 'text' : 'password'}
                        placeholder="Password"
                        title={
                            'Password must have minimum length of 8 characters and contain at least 1 lowercase character, 1 uppercase letter, 1 number and 1 special character (!@#$%^&*).'
                        }
                        ref={passwordInput}
                        required={true}
                    />
                    <div
                        className={authClasses('password-reveal', {
                            'password-reveal--active': revealPassword,
                        })}
                        onMouseDown={handlePasswordEyeClick}
                    >
                        {revealPassword && <Icon name="eye" width={20} height={20} />}
                        {!revealPassword && <Icon name="eye-off" width={20} height={20} />}
                    </div>
                    <div className={authClasses('password-banner')}>
                        Password must have minimum length of 8 characters and contain at least 1 lowercase character, 1
                        uppercase letter, 1 number and 1 special character:
                        <span>{`(^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < ' : ; | _ ~ \` + =)`}</span>
                    </div>
                </div>
            )}

            <Button className={classes('verify-button')} disabled={code.length !== 6} text="Verify" />
            <div className={classes('resend-code-wrapper')}>
                {codeTimer == 0 ? (
                    <span>
                        Not received a code?{' '}
                        <span
                            className={classes('resend-code')}
                            onClick={(e) => {
                                setCodeTimer(30)
                                resendRequest()
                            }}
                        >
                            Click here to resend
                        </span>
                    </span>
                ) : (
                    <span className={classes('timer')}>Sent! Please wait {codeTimer} seconds before trying again.</span>
                )}
            </div>
        </form>
    )
}

const processBoxMovement = async (e) => {
    if (!e.ctrlKey && e.key.length == 1) {
        e.preventDefault()
    }
    if (e.target.value.length == 0 && /^[0-9]{1}/.test(e.key)) {
        e.target.value = e.key

        try {
            e.target.nextElementSibling.focus()
        } catch (e) {}
    }
    if (e.key === 'Backspace') {
        setTimeout(() => {
            try {
                e.target.previousElementSibling.focus()
            } catch (e) {}
        }, 10)
    }

    if ((e.key === 'v' && e.ctrlKey) || (e.key === 'v' && e.metaKey)) {
        const pastedText = await navigator.clipboard.readText()
        const characterArray = pastedText.split('')
        let element = e.target
        let count = 0
        while (element) {
            element.value = characterArray[count]
            element = element.nextElementSibling
            count++
        }
    }
    if (e.key === 'ArrowLeft') {
        try {
            if (e.target.selectionStart == 0) {
                e.target.previousElementSibling.focus()
                setTimeout(() => {
                    e.target.previousElementSibling.setSelectionRange(1, 1)
                }, 1)
            }
        } catch (e) {}
    }
    if (e.key === 'ArrowRight') {
        try {
            if (e.target.selectionStart == 1) {
                e.target.nextElementSibling.focus()
            }
        } catch (e) {}
    }
    // Handling Android
    setTimeout(() => {
        if (e.key === 'Unidentified') {
            if (e.target.value.length == 0) {
                setTimeout(() => {
                    try {
                        e.target.previousElementSibling.focus()
                    } catch (e) {}
                }, 10)
            } else if (/[0-9]{1}/.test(e.target.value)) {
                setTimeout(() => {
                    try {
                        e.target.nextElementSibling.focus()
                    } catch (e) {}
                }, 10)
            }
        }
    }, 50)
}
